import { AlertTriangle, Pencil, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataCard, DetailRow } from "@/components/ui/data-card";
import { TractorDeleteButton } from "@/components/tractors/TractorDeleteButton";
import type { Location, Tractor } from "@/types/tms";

export type TractorCardTractor = Tractor & { home_base?: Location | null };

interface TractorCardProps {
  tractor: TractorCardTractor;
  isChecked: boolean;
  onToggle: (id: string) => void;
  onClick: (tractor: TractorCardTractor) => void;
  onEdit: (tractor: Tractor) => void;
  onDelete: (id: string) => void;
  odometerMiles?: number;
  isOos?: boolean;
  openServiceCount?: number;
  /** Comma-delimited eligible mission modes from the latest AAM event.
   *  Omit to hide the row (flag off / no data). */
  eligibleModes?: string;
  /** Render the eligible-modes value as a blocked/error state. */
  eligibleModesBlocked?: boolean;
}

/** Mobile/tablet representation of a tractor row on the Tractors page. */
export function TractorCard({
  tractor,
  isChecked,
  onToggle,
  onClick,
  onEdit,
  onDelete,
  odometerMiles,
  isOos,
  openServiceCount,
  eligibleModes,
  eligibleModesBlocked,
}: TractorCardProps) {
  return (
    <DataCard
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, input, a, [role="checkbox"]')) return;
        onClick(tractor);
      }}
    >
      <DataCard.Header>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => onToggle(tractor.id)}
            aria-label={`Select ${tractor.name}`}
          />
        </span>
        {isOos ? (
          <AlertTriangle className="h-4 w-4 shrink-0 fill-destructive text-background" aria-label="Out of service" />
        ) : (
          <Truck className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="font-medium truncate">{tractor.name}</span>

        {isOos ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
            <AlertTriangle className="h-3 w-3" />
            Out of Service
            {openServiceCount && openServiceCount > 1 ? (
              <span className="opacity-70">({openServiceCount})</span>
            ) : null}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">In Service</span>
        )}
        <span className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(tractor)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <TractorDeleteButton
            tractorId={tractor.id}
            tractorName={tractor.name}
            onConfirm={() => onDelete(tractor.id)}
          />
        </span>
      </DataCard.Header>

      <div className="divide-y divide-border">
        <DetailRow label="Base truck" value={tractor.base_truck || null} />
        <DetailRow label="Home base" value={tractor.home_base?.short_code || null} />
        <DetailRow
          label="VIN"
          value={tractor.vin ? <span className="font-mono text-xs">{tractor.vin}</span> : null}
        />
        <DetailRow
          label="Odometer"
          value={odometerMiles != null ? `${odometerMiles.toLocaleString()} mi` : null}
        />
        {eligibleModes ? (
          <DetailRow
            label="Eligible modes"
            value={
              <span className={eligibleModesBlocked ? "font-medium text-destructive" : undefined}>
                {eligibleModes}
              </span>
            }
          />
        ) : null}
      </div>
    </DataCard>
  );
}